import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/use-toast';
import { supabase } from '../lib/supabase';
import { Eye, EyeOff } from 'lucide-react';

export default function ResetWithOTPPage() {
  const { toast } = useToast();
  const [location, setLocation] = useLocation();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // Prefill email from query if present
    try {
      const params = new URLSearchParams(window.location.search);
      const e = params.get('email') || '';
      if (e) {
        setEmail(e);
      } else {
        try { toast({ title: 'Missing email', description: 'Please start from Forgot Password so we can send your code.', variant: 'destructive' }); } catch {}
        setLocation('/auth?mode=login');
      }
    } catch {}
    // Prompt user
    try { toast({ title: 'Check your email', description: 'Enter the 6-digit code we sent to your inbox.' }); } catch {}
  }, [toast]);

  const verifyAndReset = async () => {
    if (!email || !otp) { toast({ title: 'Missing info', description: 'Email and code required', variant: 'destructive' }); return; }
    if (!password || password !== confirmPassword) { toast({ title: 'Passwords do not match', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.auth.verifyOtp({ email, token: otp, type: 'email' });
      if (error) throw error;
      try { toast({ title: 'Code verified', description: 'Updating password…' }); } catch {}
      // Ensure session is established before update
      const { data: sessData, error: sessErr } = await supabase.auth.getSession();
      if (sessErr) throw sessErr;
      if (!sessData?.session) {
        // wait briefly and retry once
        await new Promise(r => setTimeout(r, 500));
        const retry = await supabase.auth.getSession();
        if (!retry.data?.session) throw new Error('No active session after verification');
      }
      // Now update password
      const { data: updData, error: updErr } = await supabase.auth.updateUser({ password });
      if (updErr) throw updErr;
      if (!updData?.user) throw new Error('Password update returned no user');
      toast({ title: 'Password updated', description: 'You are all set!' });
      setLocation('/', { replace: true });
    } catch (e: any) {
      const msg = e?.message || (typeof e === 'string' ? e : 'Unknown error');
      toast({ title: 'Reset failed', description: msg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-gradient-to-br from-blue-50 via-white to-blue-100 border-0 ring-0 outline-none pt-safe pb-safe">
      <div className="w-full max-w-md px-4 py-8 border-0 ring-0 outline-none">
        <Card className="w-full backdrop-blur-sm bg-white/80 shadow-xl border-0 rounded-2xl ring-0 outline-none shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
          <CardHeader className="space-y-1 border-0 ring-0 outline-none">
            <CardTitle className="text-2xl font-bold text-center bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent border-0 ring-0 outline-none">
              Reset Password
            </CardTitle>
            <p className="text-center text-sm text-gray-500 border-0 ring-0 outline-none">Enter the 6-digit code sent to your email and choose a new password.</p>
          </CardHeader>
          <CardContent className="border-0 ring-0 outline-none">
            <form onSubmit={(e) => { e.preventDefault(); verifyAndReset(); }} className="space-y-4 border-0 ring-0 outline-none">
              <div className="space-y-2 border-0 ring-0 outline-none">
                <label className="text-sm font-medium text-gray-700 border-0 ring-0 outline-none">Reset Code</label>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                  disabled={submitting}
                  className="transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border-0 shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white/95 focus:outline-none"
                />
              </div>
              <div className="space-y-2 border-0 ring-0 outline-none">
                <label className="text-sm font-medium text-gray-700 border-0 ring-0 outline-none">New Password</label>
                <div className="relative border-0 ring-0 outline-none">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={submitting}
                    className="transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border-0 shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white/95 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200 border-0 ring-0 outline-none focus:outline-none focus:ring-0 focus:border-0 appearance-none bg-transparent p-0 m-0"
                    disabled={submitting}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <div className="space-y-2 border-0 ring-0 outline-none">
                <label className="text-sm font-medium text-gray-700 border-0 ring-0 outline-none">Confirm New Password</label>
                <div className="relative border-0 ring-0 outline-none">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={submitting}
                    className="transition-all duration-200 focus:ring-2 focus:ring-blue-600/40 focus:border-blue-600/40 rounded-xl border-0 shadow-[0_4px_12px_rgba(0,0,0,0.1)] ring-0 outline-none bg-white/95 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors duration-200 border-0 ring-0 outline-none focus:outline-none focus:ring-0 focus:border-0 appearance-none bg-transparent p-0 m-0"
                    disabled={submitting}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white transition-all duration-200 transform hover:scale-[1.02] rounded-xl border-0 shadow-sm ring-0 outline-none focus:ring-2 focus:ring-blue-600/40" disabled={submitting}>
                {submitting ? 'Resetting Password…' : 'Reset Password'}
              </Button>
            </form>
          </CardContent>
        </Card>
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setLocation('/auth?mode=login')}
            className="text-sm text-gray-600 hover:text-gray-800 underline decoration-gray-300 hover:decoration-gray-600"
          >
            Back to login
          </button>
        </div>
      </div>
    </div>
  );
}


